/* @flow */

import * as React from 'react';
import {useDispatch} from 'react-redux';
import type {
    GetMcfxCompany_mcfxCompany_industry as Industry,
    GetMcfxCompany_mcfxCompany_mcfxSessions as Session,
    GetMcfxCompany_mcfxCompany_primaryAddress as Address,
} from 'nutshell-graphql-types';

import {Avatar} from 'shells/avatar';
import {TwoLine} from 'shells/two-line';
import {Divider} from 'shells/layout';
import {CloseButton} from 'shells/button';
import {Link} from 'shells/link';
import {colors} from 'shells/colors';
import {AccountIcon} from 'shells/icon';
import {LockAvatar} from 'shells/avatar/lock-avatar';
import {UnlockAvatar} from 'shells/avatar/unlock-avatar';
import {RelativeTimestamp} from 'shells/timestamp/relative-timestamp';

import {UnlockCompaniesButton} from '../unlock-companies/unlock-companies-button';
import {ConvertCompanyButton} from './convert-company-button';

import {Map} from './map';
import {closeSidebar} from './actions';
import {ProspectTimeline} from './visitor-timeline';

import './sidebar-content.css';

type Props = {
    id: string,
    name: string,
    address: ?Address,
    industry: ?Industry,
    phone: ?string,
    url: ?string,
    revenue: ?number,
    numEmployees: ?number,
    isLocked: boolean,
    sessions: Session[],
    wasConvertedAutomatically: boolean,
    convertedTime: ?number,
    unlockTime: ?number,
    lastSeenTime: ?number,
    relatedAccountPath: ?string,
    numVisits: number,
    totalVisitDuration: number,
};

export function SidebarContent(props: Props) {
    const dispatch = useDispatch();
    const onCloseSidebar = () => dispatch(closeSidebar());

    const shouldShowConvertButton =
        !props.isLocked && !props.wasConvertedAutomatically && !props.convertedTime;
    const shouldShowTimeline = !props.isLocked;

    const unknown = <i>Unknown</i>;

    const LockableTwoLine = (lockableTwoLineProps: {
        topRow: string,
        bottomRow: ?string,
        isBlue?: boolean,
    }): React.Node => {
        if (props.isLocked && lockableTwoLineProps.bottomRow) {
            return (
                <div>
                    <div>
                        <strong>{lockableTwoLineProps.topRow}</strong>
                    </div>
                    <div
                        data-tip={`${lockableTwoLineProps.topRow} will remain hidden until this company is unlocked`}
                        styleName={
                            lockableTwoLineProps.isBlue ? 'locked-text--blue' : 'locked-text'
                        }
                    >
                        {lockableTwoLineProps.bottomRow}
                    </div>
                </div>
            );
        }

        let bottomRow = lockableTwoLineProps.bottomRow;
        if (!props.isLocked && bottomRow && lockableTwoLineProps.topRow === 'URL') {
            bottomRow = (
                <Link href={bottomRow} variant='primary' size='small' newTab={true}>
                    {bottomRow}
                </Link>
            );
        }

        return <TwoLine topRow={lockableTwoLineProps.topRow} bottomRow={bottomRow || unknown} />;
    };

    const getIcon = () => {
        if (props.convertedTime) {
            return (
                <Avatar
                    type='accounts'
                    avatarUrl={undefined}
                    circular={true}
                    border={true}
                    size={60}
                />
            );
        }

        if (props.isLocked) {
            return <LockAvatar size={60} backgroundColor={colors.grey} />;
        } else {
            return <UnlockAvatar size={60} backgroundColor={colors.blue} fill={colors.white} />;
        }
    };

    const getTime = () => {
        if (props.wasConvertedAutomatically) {
            return <div styleName='time'>Found in Nutshell and unlocked automatically</div>;
        }

        if (props.convertedTime) {
            return (
                <div styleName='time'>
                    <span>Added to Nutshell</span>&nbsp;
                    <RelativeTimestamp datetime={props.convertedTime} format='fromNow' />
                </div>
            );
        }

        if (props.unlockTime) {
            return (
                <div styleName='time'>
                    <span>Unlocked</span>&nbsp;
                    <RelativeTimestamp datetime={props.unlockTime} format='fromNow' />
                </div>
            );
        } else if (props.lastSeenTime) {
            return (
                <div styleName='time'>
                    <span>Seen</span>&nbsp;
                    <RelativeTimestamp datetime={props.lastSeenTime} format='fromNow' />
                </div>
            );
        }
    };

    return (
        <div styleName='container'>
            <div styleName='top'>
                <CloseButton onClick={onCloseSidebar} />
                <Map
                    coordinates={props.address ? props.address.coordinates : undefined}
                    isBlurred={props.isLocked}
                />
                <div styleName='header'>
                    {getIcon()}
                    <div>
                        <div styleName='name'>{props.name}</div>
                        {getTime()}
                    </div>
                </div>
            </div>
            <div styleName='body'>
                <div styleName='info'>
                    <div styleName='grid' className='mb-16'>
                        <LockableTwoLine topRow='URL' bottomRow={props.url} isBlue={true} />
                        <LockableTwoLine topRow='Phone' bottomRow={props.phone} />
                    </div>
                    {props.address ? (
                        <div className='mb-16'>
                            <TwoLine
                                topRow={props.isLocked ? 'Region' : 'Full address'}
                                bottomRow={
                                    props.isLocked
                                        ? props.address.region
                                        : props.address.fullAddress
                                }
                            />
                        </div>
                    ) : (
                        undefined
                    )}
                    <div className='mb-16'>
                        <TwoLine
                            topRow='Industry'
                            bottomRow={props.industry ? props.industry.name : unknown}
                        />
                    </div>
                    <div styleName='grid'>
                        <TwoLine
                            topRow='Revenue'
                            bottomRow={
                                props.revenue ? `$${props.revenue.toLocaleString()}` : unknown
                            }
                        />
                        <TwoLine
                            topRow='# of employees'
                            bottomRow={
                                props.numEmployees ? props.numEmployees.toLocaleString() : unknown
                            }
                        />
                    </div>
                    <Divider variant='line--horizontal' spacing={16} />
                    <div styleName='grid'>
                        <TwoLine topRow='Page visits' bottomRow={`${props.numVisits}`} />
                        <TwoLine
                            topRow='Time on site'
                            bottomRow={
                                <div className='flex'>
                                    {props.totalVisitDuration}&nbsp;
                                    {props.totalVisitDuration === 1 ? 'minute' : 'minutes'}
                                </div>
                            }
                        />
                    </div>
                    {props.isLocked ? (
                        <div styleName='button-container'>
                            <div styleName='text'>See all details:</div>
                            <UnlockCompaniesButton
                                selectedCompanyIds={[props.id]}
                                isButtonBig={true}
                                isFullWidth={true}
                            />
                        </div>
                    ) : (
                        undefined
                    )}
                    {shouldShowConvertButton ? (
                        <div styleName='button-container'>
                            <ConvertCompanyButton
                                companyId={props.id}
                                companyName={props.name}
                                onCloseSidebar={onCloseSidebar}
                            />
                        </div>
                    ) : (
                        undefined
                    )}
                    {props.relatedAccountPath ? (
                        <div styleName='link-container'>
                            {/* $FlowIgnore - the AccountIcon props are different from other icons */}
                            <Link
                                variant='button-primary'
                                size='big'
                                icon={AccountIcon}
                                href={props.relatedAccountPath}
                                newTab={true}
                            >
                                View company
                            </Link>
                        </div>
                    ) : (
                        undefined
                    )}
                </div>
                {shouldShowTimeline ? (
                    <div styleName='scroll-container'>
                        <ProspectTimeline
                            sessions={props.sessions}
                            companyId={props.id}
                            companyName={props.name}
                        />
                    </div>
                ) : (
                    undefined
                )}
            </div>
        </div>
    );
}
